/*
 * Copyright (c) 2022 Pegasystems Inc.
 * All rights reserved.
 */
import { empty } from 'rxjs';

import {
  CONTAINER_NAMES,
  getDataContextName,
  isContainerSingleMode,
  getActiveContainerItemName,
  getChildContainers,
  getTargetFromContainerItemID,
  getContainerData,
  getContainerItemName,
  isContainerMultipleMode,
  getContainerItemData,
  updateParentLastUpdateTime,
  isContainerItemExists
} from '../../container/container-utils';
import {
  getActionPayload,
  getContext,
  getDirtyDetails,
  processViewResponse,
  SCOPE_TYPES
} from '../../store/state/epics/util';
import * as actionType from '../../store/state/actions';
import * as actions from '../../store/state/actions/action-creators';
import { refreshData } from '../../refresh/action-creator';
import ContextProcessor from '../../context/context-processor';
import Utils from '../../utils/utils';
import constants, { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import * as StateManager from '../../store/state/state-manager';
import {
  getCaseInfoID,
  getContainerNameFromContainerItemID,
  getNextAssignmentInfoID,
  isParentAndChildContextHoldingSameItem,
  getCaseClassName,
  isNextStepInCreateStage,
  getCurrentCaseViewMode
} from '../common-helper';
import { getParentContext, getParentContextName } from '../../utils/context-utils';
import { ActionManagerInstance } from '../../actions/manager';
import HeaderProcessor from '../../service_broker/header-processor';
import { publishAssignmentSubmissionDoneEvent } from '../common-events';
import ContextTreeManager from '../../contextree/treemanager';
import { getMessagesPath } from '../../messages';
import type { Action } from '../../store/state/state-manager';
import CaseUtils from '../utils';
import type { DxAPIResponseObject, Headers, Assignment, BlendedUICreateStageCompletedInfo } from '../types';
import { getObjectClassName } from '../../object/common-helper';
import ObjectTransformer from '../../utils/object-transformer';

const { RESOURCE_TYPES } = publicConstants;

export const getViewTypeForFinishAssignment = (actionInPayload: Action['payload']) => {
  const { isModalAction, viewType, isInCreateStage, isCaseWideAction } = actionInPayload;

  /*
    viewType=page will return pzCreateDetails view which is required in modal and case creation using /create (Case creation and submission using semantic url).
    For create stage rendering page inline send viewType as form so that response will not be pzCreateDetails and progress bar and button will appear only once in the form.
    Setting viewType to page only incase of finishAssignment of create stage in modal and and case creation using /create (Case creation and submission using semantic url).
  */
  if (isModalAction && isInCreateStage && !isCaseWideAction) {
    return SCOPE_TYPES.PAGE;
  }

  if (viewType) {
    return viewType;
  }

  return SCOPE_TYPES.FORM;
};

export const getActionNameForDxApiCall = (actionInPayload: Action['payload']) => {
  const { isCaseWideAction } = actionInPayload;
  return isCaseWideAction ? actionType.ACTION_EXECUTE_CASE_WIDE_LOCAL_ACTION : actionType.ACTION_FINISHASSIGNMENT;
};

export const addActionToActivateModalContainerItem = (actionsOut: Action[], actionInPayload: Action['payload']) => {
  const { containerItemID, context, actionMgrID, isInCreateStage, isModalAction } = actionInPayload;

  // To identify the current opened modal in ModalViewContainer, we are activating current container on every finishAssignment
  if (isInCreateStage && isModalAction) {
    const containerName = getContainerNameFromContainerItemID(containerItemID);
    const target = getResolvedTargetName(containerName, context);
    actionsOut.push(
      actions.postProcess(actions.activateContainerItem, {
        containerItemID,
        target,
        actionMgrID
      })
    );
  }
};

export const addActionToRemoveCurrentModalContainerItem = (
  actionsOut: Action[],
  actionInPayload: Action['payload']
) => {
  const { containerItemID, skipReleaseLockRequest, isInCreateStage, isProcessAction } = actionInPayload;
  const context = getDataContextName(containerItemID);
  const target = `${context?.includes('root') ? constants.APP.ROOT : constants.APP.APP}/${CONTAINER_NAMES.MODAL}`;

  actionsOut.push(
    actions.postProcess(actions.removeContainerItem, {
      containerItemID,
      context,
      target,
      skipReleaseLockRequest,
      isInCreateStage,
      isProcessAction
    })
  );
};

export const shouldClearView = (actionInPayload: Action['payload'], translatedDXAPIResponse: DxAPIResponseObject) => {
  const currentAssignment = actionInPayload?.assignmentID;
  const fetchedAssignments = translatedDXAPIResponse?.data?.caseInfo?.assignments ?? [];
  const nextAssignment = fetchedAssignments[0]?.ID;

  return currentAssignment !== nextAssignment;
};

export const canOpenCaseView = (context: string | null) => {
  return StateManager.getValue('.openCaseViewAfterCreate', constants.CONTEXT_DATA, context) ?? true;
};

export const processLastAssignmentInCreateStageModal = async (
  actionsOut: Action[],
  {
    translatedDXAPIResponse,
    actionInPayload
  }: { translatedDXAPIResponse: DxAPIResponseObject; actionInPayload: Action['payload'] },
  commonOutActionPayload: Action['payload'],
  isObjectAction: boolean,
  blendedUICreateStageCompletedInfo: BlendedUICreateStageCompletedInfo
) => {
  const { caseID } = actionInPayload;
  const assignmentId = getNextAssignmentInfoID(translatedDXAPIResponse);
  const caseType = isObjectAction
    ? getObjectClassName(translatedDXAPIResponse)
    : getCaseClassName(translatedDXAPIResponse);
  const { caseViewMode, headers, semanticURL, actionMgrID, directUrlUpdation, key, flowName, resourceType } =
    commonOutActionPayload;

  /* publish only for case created from traditional ui and not constellation local action submit */
  const containerTarget = getTargetFromContainerItemID(actionInPayload?.context);
  const isSubmitFromBlendedUI = getContainerItemData(containerTarget, actionInPayload?.context)?.acTertiary;

  Object.assign(blendedUICreateStageCompletedInfo, {
    caseId: caseID,
    assignmentId,
    caseType,
    isSubmitFromBlendedUI
  });

  /* last assignment submission crossed, remove the current opened modal container item */
  addActionToRemoveCurrentModalContainerItem(actionsOut, {
    ...actionInPayload,
    skipReleaseLockRequest: true
  });

  /* using primary target for displaying current case details */
  const primaryTarget = `${
    actionInPayload?.containerItemID.includes('root') ? constants.APP.ROOT : constants.APP.APP
  }/${CONTAINER_NAMES.PRIMARY}`;
  const primaryContext = getContext(primaryTarget, CONTAINER_NAMES.PRIMARY);

  const coreHeader = HeaderProcessor.getCoreHeadersFromContext(actionInPayload.context) || false;
  const primaryTargetPayload = getActionPayload(translatedDXAPIResponse, {
    target: primaryTarget,
    context: primaryContext,
    containerItemID: primaryContext,
    caseViewMode,
    headers,
    semanticURL,
    key,
    flowName,
    resourceType,
    directUrlUpdation,
    debugInfo: coreHeader &&
      coreHeader.debugInfo && { debugId: coreHeader.debugInfo[constants.COREHEADER_APP_DEBUG_ID] }
  });

  if (isContainerSingleMode(primaryTarget)) {
    const data = StateManager.getDataObject(primaryContext);
    const existingCaseInfoID = getCaseInfoID({ data });
    const [, existingCaseID] = existingCaseInfoID ? existingCaseInfoID.split(' ') : [];
    const dirtyMessage = `You will lose unsaved changes for ${existingCaseID}. Do you want to continue?`;
    if (canOpenCaseView(actionInPayload.context)) {
      /*
      Dirty popup should be shown only if new case is opened in case view.
      If case is created but not opened in case view dirty popup should not be shown
      */
      const { continueExecution } = getDirtyDetails(primaryTarget, { dirtyMessage });
      // If Cancel is pressed in confirm box - Do nothing
      if (!continueExecution) {
        // As we are canceling the dirty confirmation dialog, browser's location href shouldn't be updated with newly created case.
        commonOutActionPayload.directUrlUpdation = false;
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }
      /*
      If Ok is pressed in confirm box (i.e discard the changes in background which is in primary area and replace the target)
        - Should dispatch replaceContainerItem (on primary context)
    */
      primaryTargetPayload.currentCaseViewMode = getCurrentCaseViewMode(primaryTargetPayload?.context);

      actionsOut.push(actions.postProcess(actions.replaceContainerItem, primaryTargetPayload));
    } else {
      return actionsOut;
    }
  } else {
    // Should dispatch addContainerItem on primary target
    actionsOut.push(actions.postProcess(actions.addContainerItem, primaryTargetPayload));
  }

  translatedDXAPIResponse.directUrlUpdation = directUrlUpdation;
  translatedDXAPIResponse.semanticURL = semanticURL;
  await processViewResponse(
    translatedDXAPIResponse,
    {
      ...primaryTargetPayload,
      /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
      acName: primaryTarget
    },
    { acName: primaryTarget, context: primaryContext }
  );
  return actionsOut;
};

export const addActionToRefreshCasePreviewView = (
  actionsOut: Action[],
  actionInPayload: Action['payload'],
  options: { caseClassName: string }
) => {
  const isStartWithPreviewContext = /^app\/preview_[0-9]*/g;
  const { containerItemID, caseID } = actionInPayload;
  if (containerItemID && isStartWithPreviewContext.test(containerItemID)) {
    const { caseClassName } = options;
    const parentContainerItemID = getParentContextName(containerItemID);
    const parentContext = getDataContextName(parentContainerItemID); // e.g app/preview_1
    actionsOut.push(
      actions.postProcess(actions.loadView, {
        containerItemID: parentContainerItemID,
        context: parentContext,
        container: CONTAINER_NAMES.PREVIEW, // [TODO]: need to check why container word is been used instead of containerName
        containerName: CONTAINER_NAMES.PREVIEW,
        caseId: caseID,
        viewName: publicConstants.PREVIEW_VIEW_NAME,
        caseClassName,
        updateData: true
      })
    );
  }
};

/**
 * this function helps to merge response from child container to parent container when the same case data is being shared between both
 * @param actionsOut - actions which will get dispatched out
 * @param actionInPayload -incoming action payload from public action api
 * @param options - optional data
 * @private
 */
export const addActionToMergeResponseDataToParentContext = (
  actionsOut: Action[],
  actionInPayload: Action['payload'],
  { data, caseViewMode }: { [key: string]: any }
) => {
  const { containerItemID } = actionInPayload;
  const childContext = getDataContextName(containerItemID);

  if (childContext && isParentAndChildContextHoldingSameItem(containerItemID)) {
    const parentContext = getParentContext(childContext);
    const parentPayload = {
      data,
      caseViewMode,
      context: parentContext
    };
    Utils.setIn(parentPayload.data, ['context_data', 'caseViewMode'], caseViewMode);
    actionsOut.push({
      type: actionType.DELETE_CASE_SUMMARY,
      payload: {
        context: parentContext
      }
    });
    const propertiesToReplace = CaseUtils.getPropertiesToReplace(containerItemID);
    actionsOut.push(actions.postProcess(actions.mergeData, parentPayload, { propertiesToReplace }));
  }
};

export const getPayloadForEndOfAssignmentProcessingEvent = (
  translatedDXAPIResponse: DxAPIResponseObject,
  actionInPayload: Action['payload']
) => {
  const { isCaseWideAction, assignmentID, actionID } = actionInPayload;

  return {
    isCaseWideAction,
    caseID: getCaseInfoID(translatedDXAPIResponse),
    assignmentID: !isCaseWideAction ? assignmentID : null,
    actionID: actionID || null
  };
};

export const addRemoveTransientItemAction = (
  actionsOut: Action[],
  { isModalAction, target, transientItemID }: { isModalAction: boolean; target: string | null; transientItemID: string }
) => {
  if (isModalAction) {
    const replaceAction = actionsOut.find(({ type }) => type === actionType.REPLACE_CONTAINER_ITEM);
    if (replaceAction?.payload) {
      target = replaceAction.payload.target;
      transientItemID = replaceAction.payload.containerItemID;
    }
  }
  const removeAllTransientItems = actions.removeAllTransientItems({
    target,
    containerItemID: transientItemID
  });
  if (removeAllTransientItems.payload.transientItems.length) {
    actionsOut.push(removeAllTransientItems);
  }
};

export const createPayloadForOpenWorkByHandle = (
  parentCaseInfo: { ID: string; content: { classID: string } },
  actionInPayload: Action['payload'],
  actionsOut: Action[]
) => {
  if (actionInPayload.isProcessAction) {
    addActionToRemoveCurrentModalContainerItem(actionsOut, {
      ...actionInPayload,
      skipReleaseLockRequest: true
    });
    if (parentCaseInfo) {
      const primaryTarget = `${
        actionInPayload?.containerItemID.includes('root') ? constants.APP.ROOT : constants.APP.APP
      }/${CONTAINER_NAMES.PRIMARY}`;
      const primaryContext = getContext(primaryTarget, CONTAINER_NAMES.PRIMARY);
      const openWorkByHandleActionInfo = {
        insKey: parentCaseInfo.ID,
        className: parentCaseInfo.content.classID,
        context: primaryContext,
        acTargetName: constants.PRIMARY,
        isChild: true
      };
      const openWorkByHandle = {
        type: actionType.OPEN_WORK_BY_HANDLE,
        payload: { ...openWorkByHandleActionInfo }
      };
      actionsOut.push(openWorkByHandle);
    }
  }
};

export const isModalLaunchedFromPreview = (context: string) => {
  const isStartWithPreviewContext = /^app\/preview_[0-9]*/g;
  return isStartWithPreviewContext.test(context);
};

export const isModalLaunchedFromPrimary = (context: string, activeWorkArea: string | null) => {
  const isStartWithPrimaryContext = /^app\/primary_[0-9]*/g;
  const parentContext = getParentContextName(context);
  if (isModalLaunchedFromPreview(context)) {
    return false;
  }
  if (parentContext === activeWorkArea) {
    return true;
  }
  return isStartWithPrimaryContext.test(parentContext);
};

export const createToastMessage = (payload: Action['payload']) => {
  if (!canOpenCaseView(payload.context)) {
    let caseID: string;
    const caseTypeID = payload?.caseTypeID;
    const caseTypeName = payload?.caseTypeName;
    if (payload.businessID) {
      caseID = payload.businessID;
    } else {
      caseID = payload.workID;
    }

    return { caseID, caseTypeID, caseTypeName };
  }

  return {};
};

export const getContainerIdByCaseId = (context: string, caseID: string) => {
  const containerTarget = getTargetFromContainerItemID(context);
  return getContainerItemName(containerTarget, caseID);
};

export const createPayloadForRefreshAssignment = (
  context: string | null,
  caseWideAction = false,
  contextData = false
) => {
  const pageReference = '';
  const actionMgrID = ActionManagerInstance.getNextActionMgrId();
  const ID = ContextProcessor.getPropertyValue(publicConstants.CASE_INFO.ASSIGNMENT_ID, pageReference, context);
  const actionID = ContextProcessor.getPropertyValue(
    publicConstants.CASE_INFO.ASSIGNMENTACTION_ID,
    pageReference,
    context
  );

  return {
    ID,
    actionID,
    context,
    caseWideAction,
    contextData,
    options: {
      actionMgrID
    }
  };
};

const addDeleteCaseSummaryAndMergeDataActions = (
  actionsOut: Action['payload'],
  commonOutActionPayload: Action['payload'],
  parentContainerItemID: string,
  isObjectAction: boolean
) => {
  const { data, caseViewMode } = commonOutActionPayload;
  const parentPayload = {
    data,
    caseViewMode,
    context: parentContainerItemID
  };
  Utils.setIn(parentPayload.data, ['context_data', 'caseViewMode'], caseViewMode);
  actionsOut.push({
    type: actionType.DELETE_CASE_SUMMARY,
    payload: {
      context: parentContainerItemID
    }
  });
  // If this is object action, then merge content to caseInfo
  if (isObjectAction) {
    const objectData = ObjectTransformer.transformObjectToCase(data);
    const objectPayload = {
      data: objectData,
      caseViewMode,
      context: parentContainerItemID
    };
    actionsOut.push(actions.postProcess(actions.mergeData, objectPayload));
  }
  actionsOut.push(actions.postProcess(actions.mergeData, parentPayload));
};

export const addActionsForLocalActionLaunchedFromPrimary = async (
  activeWorkArea: string | null,
  commonOutActionPayload: Action['payload'],
  parentContainerItemID: string,
  actionInPayload: Action['payload'],
  actionsOut: Action[],
  isNextAssignmentInfoPresent: boolean,
  keyValue: string,
  translatedDXAPIResponse: DxAPIResponseObject,
  chainedData: { type: string; headers: any; actionInPayload: Action['payload']; response?: DxAPIResponseObject },
  isObjectAction: boolean
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const { headers } = chainedData;
  const info = isObjectAction ? constants.OBJECT_INFO.OBJECT_INFO : constants.CASE_INFO.CASE_INFO;
  // active workarea is null when we launch the localaction from interaction in multidoc mode
  if (!activeWorkArea) {
    addActionToRemoveCurrentModalContainerItem(actionsOut, actionInPayload);
    let isParentContextHoldingTheSameItem = false;
    const parentTarget = getTargetFromContainerItemID(parentContainerItemID);
    const parentContainersData = getContainerData(parentTarget);
    const activeContainerItem = getActiveContainerItemName(parentTarget);

    if (
      activeContainerItem &&
      parentContainersData?.items?.[activeContainerItem]?.key === chainedData?.actionInPayload?.caseID
    ) {
      isParentContextHoldingTheSameItem = true;
    }
    if (isNextAssignmentInfoPresent) {
      const parentContainerTarget = getChildContainers(parentContainerItemID)[0];
      actionInPayload.target = parentContainerTarget;
      actionInPayload.context = getContext(parentContainerTarget, constants.WORKAREA, parentContainerItemID);
      actionInPayload.containerItemID = actionInPayload.context;
      actionsOut.push(
        actions.postProcess(actions.addContainerItem, {
          ...actionInPayload,
          ...commonOutActionPayload
        })
      );
      const containerItemIDValue = actionInPayload.containerItemID;
      actionsOut.push(
        actions.postProcess(actions.activateContainerItem, {
          parentContainerTarget,
          containerItemID: containerItemIDValue
        })
      );
    }
    if (isParentContextHoldingTheSameItem) {
      addDeleteCaseSummaryAndMergeDataActions(
        actionsOut,
        commonOutActionPayload,
        parentContainerItemID,
        isObjectAction
      );
    }
  }
  // Launching the localaction from caseview in multidoc mod when multiple containers are present
  else {
    if (typeof chainedData?.response?.uiResources?.root !== 'object') {
      const rearrangeById = (arr: Assignment[], id: string) => {
        // Find the index of the object with the specified ID
        const index = arr.findIndex((obj: Assignment) => obj.ID === id);

        // If the object is found, move it to the 0 index
        if (index !== -1) {
          const [item] = arr.splice(index, 1);
          arr.unshift(item);
        }
        return arr;
      };
      const activeCaseData = StateManager.getDataObject(activeWorkArea);

      const fetchedAssignments = chainedData?.response?.data?.caseInfo?.assignments ?? [];
      const activeCaseAssignments = activeCaseData?.caseInfo?.assignments ?? [];

      const isPerformMode = getCurrentCaseViewMode(activeWorkArea) === constants.PERFORM_MODE;
      const hasAssignmentsChanged = fetchedAssignments[0]?.ID !== activeCaseAssignments[0]?.ID;
      const hasValidAssignments = fetchedAssignments.length > 0 && activeCaseAssignments.length > 0;

      if (
        isPerformMode &&
        hasAssignmentsChanged &&
        hasValidAssignments &&
        chainedData?.response?.data?.caseInfo?.assignments
      ) {
        chainedData.response.data.caseInfo.assignments = rearrangeById(
          fetchedAssignments,
          activeCaseAssignments[0]?.ID
        );
      }
    }
    const isContainerSingleModeValue = isContainerSingleMode(getTargetFromContainerItemID(activeWorkArea));
    let isParentContextHoldingTheSameItem = false;
    const parentTarget = getTargetFromContainerItemID(parentContainerItemID);
    const parentContainersData = getContainerData(parentTarget);
    const activeContainerItem = getActiveContainerItemName(parentTarget);
    if (
      activeContainerItem &&
      parentContainersData?.items?.[activeContainerItem]?.key === chainedData?.actionInPayload?.caseID
    ) {
      isParentContextHoldingTheSameItem = true;
    }
    addActionToRemoveCurrentModalContainerItem(actionsOut, {
      ...actionInPayload,
      skipReleaseLockRequest: true
    });
    const newContainerName = getContainerNameFromContainerItemID(activeWorkArea);
    const currentContainerInfoObj = {
      target: getTargetFromContainerItemID(activeWorkArea),
      containerItemID: activeWorkArea,
      context: activeWorkArea,
      containerName: newContainerName
    };
    commonOutActionPayload.doDeleteCaseSummary = actionInPayload.doDeleteCaseSummary;
    const parentContext = getParentContext(parentContainerItemID);
    const parentContainerData = StateManager.getDataObject(parentContext);
    const currentContainerData = StateManager.getDataObject(actionInPayload.context);
    const parentContainerCaseID = parentContainerData[info]?.ID;
    const currentContainerCaseID = currentContainerData[info]?.ID;
    const activeWorkAreaCaseId = StateManager.getDataObject(activeWorkArea)?.caseInfo?.ID;
    if (
      (isContainerSingleModeValue && parentContainerCaseID === currentContainerCaseID) ||
      (!isContainerSingleModeValue &&
        (keyValue === '' ||
          (currentContainerInfoObj.containerItemID.includes(constants.WORKAREA) &&
            commonOutActionPayload.caseViewMode === 'review') ||
          (!isNextAssignmentInfoPresent &&
            commonOutActionPayload.root &&
            Object.keys(commonOutActionPayload.root).length === 0 &&
            activeWorkAreaCaseId === commonOutActionPayload.data?.caseInfo?.ID)))
    ) {
      if (
        commonOutActionPayload?.data[info]?.content === undefined ||
        (commonOutActionPayload.data[info].content !== undefined &&
          typeof commonOutActionPayload.data[info].content === 'object' &&
          Object.keys(commonOutActionPayload.data[info].content).length === 0)
      ) {
        commonOutActionPayload.root = undefined;
        delete commonOutActionPayload.data?.caseMessages;
      }
      /*
       * When case is being moved to review mode using local action where current assignment and next assignment are not same
       * Usecase ::
       * When there is an active assignment and user performed processAction to launch a new assignment
       * New assignment launched using processAction is dismissed using localAction(eg: pxDismiss)
       * Workarea is set to review mode and view of workarea needs to be cleared from store.
       */
      if (
        activeWorkArea.includes(constants.WORKAREA) &&
        commonOutActionPayload.caseViewMode === constants.REVIEW_MODE &&
        shouldClearView(actionInPayload, translatedDXAPIResponse)
      ) {
        commonOutActionPayload.flowName = ' ';
        commonOutActionPayload.clearView = true;
      }
      actionsOut.push(
        actions.postProcess(actions.updateContainerItem, {
          ...commonOutActionPayload,
          key: keyValue,
          resourceType: '',
          ...currentContainerInfoObj
        })
      );
    } else if (isNextAssignmentInfoPresent) {
      const containerTarget = getTargetFromContainerItemID(activeWorkArea);
      const containerItemName = getContainerItemName(containerTarget, keyValue);
      if (containerItemName) {
        currentContainerInfoObj.target = containerTarget;
        currentContainerInfoObj.context = containerItemName;
        currentContainerInfoObj.containerItemID = containerItemName;

        actionsOut.push(
          actions.postProcess(actions.updateContainerItem, {
            ...commonOutActionPayload,
            key: keyValue,
            resourceType: '',
            ...currentContainerInfoObj
          })
        );
      } else {
        const parentContainerTarget = getChildContainers(parentContainerItemID)[0];
        actionInPayload.target = parentContainerTarget;
        actionInPayload.context = getContext(parentContainerTarget, constants.WORKAREA, parentContainerItemID);
        if (actionInPayload?.context) {
          actionInPayload.containerItemID = actionInPayload.context;
          actionsOut.push(
            actions.postProcess(actions.addContainerItem, {
              ...actionInPayload,
              ...commonOutActionPayload
            })
          );
          const containerItemIDValue = actionInPayload.containerItemID;
          actionsOut.push(
            actions.postProcess(actions.activateContainerItem, {
              parentContainerTarget,
              containerItemID: containerItemIDValue
            })
          );
        }
      }
    }
    actionInPayload.target = getTargetFromContainerItemID(activeWorkArea);
    actionInPayload.containerName = getContainerNameFromContainerItemID(activeWorkArea);
    actionInPayload.context = activeWorkArea;
    actionInPayload.containerItemID = activeWorkArea;

    Utils.setIn(commonOutActionPayload, 'acName', currentContainerInfoObj.target);
    await processViewResponse(translatedDXAPIResponse, commonOutActionPayload);

    /* merge dxapi response data to parent context  */
    if (isParentContextHoldingTheSameItem) {
      if (
        parentContainerItemID.includes(constants.WORKAREA) &&
        isParentAndChildContextHoldingSameItem(parentContainerItemID)
      ) {
        parentContainerItemID = getParentContext(parentContainerItemID);
      }
      addDeleteCaseSummaryAndMergeDataActions(
        actionsOut,
        commonOutActionPayload,
        parentContainerItemID,
        isObjectAction
      );
    }

    if (!isNextAssignmentInfoPresent && commonOutActionPayload.caseViewMode === constants.PERFORM_MODE) {
      // Merge latest headers to respective workarea
      const containerItemName = getContainerIdByCaseId(activeWorkArea, keyValue);
      const parentPayload = {
        data: {
          [info]: {
            headers
          }
        },
        context: containerItemName
      };
      actionsOut.push(actions.postProcess(actions.mergeData, parentPayload));

      // Refresh assignment to fetch latest details
      actionsOut.push(refreshData(createPayloadForRefreshAssignment(containerItemName)));
    }
  }
};

export const canUpdateAssignmentData = (
  validAssignment: boolean | undefined,
  context: string | null,
  translatedDXAPIResponse: DxAPIResponseObject,
  isModalAction: boolean,
  canPerform?: boolean
) => {
  let containerItemID;
  const target = getTargetFromContainerItemID(context);
  if (isContainerMultipleMode(target)) {
    containerItemID = getContainerItemName(target, translatedDXAPIResponse.data?.caseInfo?.ID);
  } else containerItemID = getActiveContainerItemName(target);

  return (
    isModalAction &&
    validAssignment &&
    StateManager.getContextValue(null, constants.CASE_VIEW_MODE_PROP, containerItemID) === constants.PERFORM_MODE &&
    canPerform
  );
};

export const processFinishAssignmentForCreateStage = async (
  response: any,
  actionInPayload: Action['payload'],
  commonOutActionPayload: Action['payload'],
  translatedDXAPIResponse: DxAPIResponseObject,
  actionsOut: Action[],
  isObjectAction: boolean,
  blendedUICreateStageCompletedInfo: BlendedUICreateStageCompletedInfo
) => {
  const info = isObjectAction ? 'objectInfo' : 'caseInfo';
  createPayloadForOpenWorkByHandle(response.data[info].parentCaseInfo, actionInPayload, actionsOut);
  const parentCaseInfoID = response?.data[info]?.parentCaseInfo?.ID;
  if (parentCaseInfoID) {
    const containerTarget = getTargetFromContainerItemID(actionInPayload?.context);
    const parentContext = getContainerItemData(containerTarget, actionInPayload?.context)?.parentContext;
    const parentTarget = getTargetFromContainerItemID(parentContext);
    if (actionInPayload?.isProcessAction && isContainerItemExists(parentTarget, parentCaseInfoID)) {
      updateParentLastUpdateTime(parentContext, response?.data[info]?.lastUpdateTime);
    }
  }
  commonOutActionPayload.resourceType = RESOURCE_TYPES.CASE;
  commonOutActionPayload.directUrlUpdation = true;
  if (!actionInPayload.isProcessAction) {
    await processLastAssignmentInCreateStageModal(
      actionsOut,
      {
        translatedDXAPIResponse,
        actionInPayload
      },
      commonOutActionPayload,
      isObjectAction,
      blendedUICreateStageCompletedInfo
    );
  }
};

/*   In Coexistence a case is opened with mashup api pyEmbed View.
 *  At the end of create stage the case view is changed to full case view aka pyDetails.
 *  This Function takes case of the same by dispatching openAssignment redux action.
 *
 *  */
export const processFinishAssignmentWithOpenAssignment = async (
  response: any,
  actionsOut: Action[],
  context: string
) => {
  const openAssignmentActionInfo = {
    assignmentID: response?.data?.caseInfo?.assignments[0]?.ID,
    className: response?.data?.caseInfo?.content?.classID,
    containerName: CONTAINER_NAMES.PRIMARY,
    targetContainerName: CONTAINER_NAMES.PRIMARY,
    viewType: SCOPE_TYPES.PAGE,
    isLastStepInCreateStage: true,
    context
  };
  const openAssignment = {
    type: actionType.OPEN_ASSIGNMENT,
    payload: { ...openAssignmentActionInfo }
  };
  actionsOut.push(openAssignment);
};

export const addActionsForFinishAssignmentOfNonCreateStages = async (
  commonOutActionPayload: { [key: string]: any },
  actionInPayload: Action['payload'],
  translatedDXAPIResponse: DxAPIResponseObject,
  currentContainerInfo: { [key: string]: any },
  actionsOut: Action[]
) => {
  /*
      Use Case:  ==> finish assignment for below cases
        - a. create stage in modal or
        - b. finish assignment of non create stages or
        - c. page inline or
        - d. triggered from any other custom places through actionsApi
    */
  commonOutActionPayload.doDeleteCaseSummary = actionInPayload.doDeleteCaseSummary;
  // flowName will be empty as reviewMode don't display the assignment form.
  if (commonOutActionPayload.caseViewMode === constants.REVIEW_MODE) {
    commonOutActionPayload.flowName = ' ';
    commonOutActionPayload.clearView = true;
  }
  const { containerItemID } = actionInPayload;
  const propertiesToReplace = CaseUtils.getPropertiesToReplace(containerItemID);
  actionsOut.push(
    actions.postProcess(actions.updateContainerItem, {
      ...commonOutActionPayload,
      ...currentContainerInfo,
      propertiesToReplace
    })
  );

  /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
  Utils.setIn(commonOutActionPayload, 'acName', currentContainerInfo.target);
  await processViewResponse(translatedDXAPIResponse, commonOutActionPayload);

  /* merge dxapi response data to parent context  */
  addActionToMergeResponseDataToParentContext(actionsOut, actionInPayload, commonOutActionPayload);
};

export const addActionsForFinishAssignmentLaunchedFromModal = async (
  actionInPayload: Action['payload'],
  commonOutActionPayload: Action['payload'],
  isNextAssignmentInfoPresent: boolean,
  keyValue: string,
  translatedDXAPIResponse: DxAPIResponseObject,
  actionsOut: Action[],
  chainedData: { type: string; headers: Headers; actionInPayload: Action['payload']; response: DxAPIResponseObject },
  response: any,
  isObjectAction: boolean,
  blendedUICreateStageCompletedInfo: BlendedUICreateStageCompletedInfo = {}
) => {
  const { isCaseWideAction, isInCreateStage, containerItemID } = actionInPayload;
  const parentContainerItemID = getParentContextName(actionInPayload.context);
  const isNextStepInCreateStageVariable = isNextStepInCreateStage(translatedDXAPIResponse, isObjectAction);
  const caseClassName = isObjectAction
    ? getObjectClassName(translatedDXAPIResponse)
    : getCaseClassName(translatedDXAPIResponse);
  const isLastAssignmentInCreateStage = isInCreateStage && !isNextStepInCreateStageVariable && !isCaseWideAction;
  const childContainers = getChildContainers(parentContainerItemID)[0];
  const activeWorkArea = childContainers ? getActiveContainerItemName(childContainers) : parentContainerItemID;
  const containerName = getContainerNameFromContainerItemID(containerItemID);
  const actionInContext = getDataContextName(containerItemID);

  const currentContainerInfo = Object.freeze({
    target: actionInPayload.target,
    containerItemID,
    context: actionInContext,
    containerName
  });

  // Use Case: 1 ==> Last finish assignment of create stage in modal dialog
  if (isLastAssignmentInCreateStage && !isModalLaunchedFromPreview(actionInPayload.context)) {
    await processFinishAssignmentForCreateStage(
      response,
      actionInPayload,
      commonOutActionPayload,
      translatedDXAPIResponse,
      actionsOut,
      isObjectAction,
      blendedUICreateStageCompletedInfo
    );
  }
  // Local action finish assignment for below use cases - local action launched from primary
  else if (isModalLaunchedFromPrimary(actionInPayload.context, activeWorkArea)) {
    await addActionsForLocalActionLaunchedFromPrimary(
      activeWorkArea,
      commonOutActionPayload,
      parentContainerItemID,
      actionInPayload,
      actionsOut,
      isNextAssignmentInfoPresent,
      keyValue,
      translatedDXAPIResponse,
      chainedData,
      isObjectAction
    );
  } else if (
    (isCaseWideAction || isObjectAction) &&
    !isModalLaunchedFromPrimary(actionInPayload.context, activeWorkArea)
  ) {
    // removing current active modal, as the local action submission is finished
    addActionToRemoveCurrentModalContainerItem(actionsOut, actionInPayload);

    /* refresh if the current local action triggered from preview container */
    addActionToRefreshCasePreviewView(actionsOut, actionInPayload, { caseClassName });
  } else {
    await addActionsForFinishAssignmentOfNonCreateStages(
      commonOutActionPayload,
      actionInPayload,
      translatedDXAPIResponse,
      currentContainerInfo,
      actionsOut
    );
  }
};

export const publishEventsForLocalActionFromPreviewView = (
  commonOutActionPayload: Action['payload'],
  actionInPayload: Action['payload'],
  activeWorkArea: string | null
) => {
  const { isModalAction, isCaseWideAction, caseID } = actionInPayload;
  const isModalLaunchedFromPrimaryValue = isModalLaunchedFromPrimary(actionInPayload.context, activeWorkArea);

  const stages = commonOutActionPayload?.data?.caseInfo?.stages;
  const stageID = commonOutActionPayload?.data?.caseInfo?.stageID;

  publishAssignmentSubmissionDoneEvent({
    caseID,
    isModalAction,
    isModalLaunchedFromPrimaryValue,
    isCaseWideAction,
    stages,
    stageID
  });
};

export const canPerformCurrentAssignment = (
  translatedDXAPIResponse: DxAPIResponseObject,
  actionInPayload: Action['payload']
) => {
  const { assignmentID } = actionInPayload;

  return translatedDXAPIResponse?.data?.caseInfo?.assignments?.some(
    (assignment) => assignment.ID === assignmentID && assignment?.canPerform === 'true'
  );
};

export const mutateErroneousFields = (errorObject: { [key: string]: any }) => {
  const allErrors = errorObject?.payload?.err?.errorDetails;
  const context = errorObject?.payload?.context;
  allErrors?.forEach((err: { [key: string]: any }) => {
    err[constants.ERROR_FIELD_IN_PAGE] = err[constants.ERROR_FIELD_IN_PAGE].replace(
      /(\(\d+\))/g,
      (str: string) => `[${parseInt(str.substring(1, str.length - 1), 10) - 1}]`
    );
    const fieldInPageWithoutDot = err[constants.ERROR_FIELD_IN_PAGE].slice(1);
    const fieldIdentifier = err[constants.ERROR_IN_PAGE_IDENTIFIER];
    const fieldIdentifierWithoutDot: string = fieldIdentifier.startsWith('.')
      ? fieldIdentifier.slice(1)
      : fieldIdentifier;

    if (fieldIdentifierWithoutDot) {
      let pageReference = fieldInPageWithoutDot ? `caseInfo.content.${fieldInPageWithoutDot}` : 'caseInfo.content';
      pageReference = constants.getProperty(pageReference.replace(/\(/g, '[').replace(/\)/g, ']'));
      const messagePath = getMessagesPath(fieldIdentifierWithoutDot, pageReference, constants.PROPERTY);

      const newError = {
        type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
        message: err.localizedValue
      };

      ContextTreeManager.mutateField(context, pageReference, messagePath[messagePath.length - 1], {
        messages: [newError],
        type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
        fieldName: fieldIdentifier
      });
    }
  });
};
